import React from 'react';
import { ordersApi } from '@/services/api';
import { formatDate, formatDateTime, formatSummaryAmount } from '@/utils/formatters';
import BaseInProgressOrders from '@/components/orders/BaseInProgressOrders';
import SchemeLabel from '@/components/orders/SchemeLabel';
import { clientScopeLabel } from '@/components/order_management/orderUtils';
import styles from '@/components/orders/OrderManagement.module.css';
import { CASH_ENV_PERCENTAGE } from './etfOrderUtils';

interface EtfInProgressOrder {
  id: number;
  order_date: string;
  created_at: string;
  scheme: { id: number; code: string; base_currency?: string; region?: number | null } | null;
  company: { id: number; symbol: string } | null;
  cash_env: number | null;
  cash_env_display: string | null;
  limit_price: string | null;
  // Always present (non-null model columns, serialized as numbers); only meaningful on a Percentage order.
  /** Buy Percentage order: % of AUM. */
  increase_by_1: number;
  /** Sell Percentage order: % of the ETF holding. */
  condition_1: number;
  /** Selected clients on a client-wise order; 0 means the whole scheme. */
  client_count?: number;
}

// A Percentage order keeps its % in increase_by_1 (buy) / condition_1 (sell).
const buyCashEnvLabel = (order: EtfInProgressOrder) =>
  order.cash_env === CASH_ENV_PERCENTAGE ? `Percentage (${order.increase_by_1}% of AUM)` : order.cash_env_display ?? '—';

const sellCashEnvLabel = (order: EtfInProgressOrder) =>
  order.cash_env === CASH_ENV_PERCENTAGE ? `Percentage (${order.condition_1}% of holding)` : order.cash_env_display ?? '—';

interface EtfInProgressOrdersProps {
  onProcessSuccess: (remaining: number) => void;
}

const EtfInProgressOrders: React.FC<EtfInProgressOrdersProps> = ({ onProcessSuccess }) => (
  <BaseInProgressOrders<EtfInProgressOrder, EtfInProgressOrder>
    titlePrefix="ETF"
    supportsBlockTrade={false}
    fetchBuyOrders={() => ordersApi.getInProgressEtfBuyOrders()}
    fetchSellOrders={() => ordersApi.getInProgressEtfSellOrders()}
    processBuyOrders={(data) => ordersApi.processEtfBuyOrders(data)}
    processSellOrders={(data) => ordersApi.processEtfSellOrders(data)}
    onProcessSuccess={(_blockTrade, remaining) => onProcessSuccess(remaining)}
    renderSharedHeaders={() => (
      <>
        <th>Order Date</th>
        <th>Scheme</th>
        <th>Clients</th>
        <th>Company</th>
        <th>Cash Environment</th>
      </>
    )}
    renderSharedBuyRowCells={(order) => (
      <>
        <td>{formatDate(order.order_date)}</td>
        <td><SchemeLabel scheme={order.scheme} /></td>
        <td>{clientScopeLabel(order.client_count)}</td>
        <td>{order.company?.symbol ?? '—'}</td>
        <td>{buyCashEnvLabel(order)}</td>
      </>
    )}
    renderSharedSellRowCells={(order) => (
      <>
        <td>{formatDate(order.order_date)}</td>
        <td><SchemeLabel scheme={order.scheme} /></td>
        <td>{clientScopeLabel(order.client_count)}</td>
        <td>{order.company?.symbol ?? '—'}</td>
        <td>{sellCashEnvLabel(order)}</td>
      </>
    )}
    renderBuyHeaders={() => (
      <th className={styles.num}>Limit Price</th>
    )}
    renderSellHeaders={() => null}
    renderBuyRow={(order) => (
      <td className={`${styles['text-mono']} ${styles.num}`}>
        {order.limit_price ? formatSummaryAmount(order.limit_price, order.scheme?.base_currency ?? null) : '—'}
      </td>
    )}
    renderSellRow={() => null}
    renderTrailingHeaders={() => <th>Created</th>}
    renderTrailingBuyRowCells={(order) => <td>{formatDateTime(order.created_at)}</td>}
    renderTrailingSellRowCells={(order) => <td>{formatDateTime(order.created_at)}</td>}
  />
);

export default EtfInProgressOrders;